// Pure helpers for the Watchers page (T-132) and the running indicator / ETA (T-254), testable with node --test (frontend/tests/watchers.test.mjs).

export const STATUS_TONE = {
  RUNNING: '#38bdf8', OK: '#34d399', IDLE: '#64748b', UNKNOWN: '#94a3b8', LATE: '#fbbf24', FAILED: '#fb7185', PAUSED: '#60a5fa', DISABLED: '#475569',
}
export const STATUS_ORDER = ['FAILED', 'LATE', 'PAUSED', 'UNKNOWN', 'OK', 'IDLE', 'DISABLED']
const KIND_ORDER = ['cron', 'job', 'thread', 'task', 'service', 'workflow', 'routine']
export const KIND_LABEL = {
  // Linux cutover 2026-11-01, Doc 50: wording no longer assumes Railway (T-323)
  cron: 'Scheduled cron (Linux timer; Railway cron if rolled back)', job: 'Cron job', thread: 'Backend worker', task: 'Backend task', service: 'Service', workflow: 'GitHub workflow', routine: 'Claude routine',
}

export function formatAge(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—'
  const s = Math.max(0, Math.round(Number(seconds)))
  if (s < 90) return `${s}s ago`
  if (s < 5400) return `${Math.round(s / 60)} min ago`
  if (s < 172800) return `${Math.round(s / 3600)} h ago`
  return `${Math.round(s / 86400)} d ago`
}

export function formatDuration(ms) {
  if (ms == null) return ''
  if (ms < 1000) return `${ms} ms`
  if (ms < 90000) return `${(ms / 1000).toFixed(1)} s`
  return `${Math.round(ms / 60000)} min`
}

export function tokenLabel(t) {
  if (t === 'run') return { text: 'YES · per run', tone: 'bad' }
  if (t === 'request') return { text: 'only on request', tone: 'warn' }
  return { text: 'NO', tone: 'good' }
}

/** Filter by kind ('all' or a kind) and a free-text query over name, id, description and schedule; group by kind; worst status first. */
export function groupWatchers(list, kind = 'all', query = '') {
  const q = String(query || '').trim().toLowerCase()
  const rows = (list || []).filter(w => (kind === 'all' || w.kind === kind) &&
    (!q || [w.name, w.id, w.description, w.schedule, w.host].some(x => String(x || '').toLowerCase().includes(q))))
  const groups = {}
  for (const w of rows) (groups[w.kind] = groups[w.kind] || []).push(w)
  const rank = (s) => { const i = STATUS_ORDER.indexOf(s); return i < 0 ? 99 : i }
  return KIND_ORDER.filter(k => groups[k]).map(k => ({
    kind: k, label: KIND_LABEL[k] || k,
    rows: groups[k].slice().sort((a, b) => rank(a.status) - rank(b.status) || String(a.name).localeCompare(String(b.name))),
  }))
}

/** One line for the summary strip: how many need attention. */
export function attentionSummary(counts) {
  const c = counts || {}
  const bad = (c.FAILED || 0), late = (c.LATE || 0), paused = (c.PAUSED || 0)
  const parts = []
  if (bad) parts.push(`${bad} failed`)
  if (late) parts.push(`${late} late`)
  if (paused) parts.push(`${paused} paused`)
  return parts.length ? parts.join(' · ') : 'all reporting watchers are on schedule'
}

/** Pause durations offered in the UI (minutes). The server caps at 24 h whatever is sent. */
export const PAUSE_CHOICES = [[60, '1 hour'], [240, '4 hours (default)'], [720, '12 hours'], [1440, '24 hours (maximum)']]

// ---- Running now and ETA (T-254). The server sends eta_s as of `as_of`; the page counts it down locally between polls. ----

/** "finishing", "40 s", "3 min", "1.5 h" for a number of seconds left. null -> "unknown". */
export function formatEta(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return 'unknown'
  const s = Number(seconds)
  if (s <= 1) return 'finishing'
  if (s < 90) return `${Math.round(s)} s`
  if (s < 5400) return `${Math.round(s / 60)} min`
  return `${(s / 3600).toFixed(1)} h`
}

/** Seconds left for one running item right now: the server's eta_s minus the time since the poll. null when the server had no ETA. */
export function remainingNow(item, fetchedAtMs, nowMs) {
  if (!item || item.eta_s == null) return null
  return Math.max(0, item.eta_s - Math.max(0, (nowMs - fetchedAtMs) / 1000))
}

/** One line for a running item: elapsed, time left (or why there is none), and what the estimate rests on. */
export function itemLine(item, fetchedAtMs, nowMs) {
  const elapsed = (item.elapsed_s || 0) + Math.max(0, (nowMs - fetchedAtMs) / 1000)
  const left = remainingNow(item, fetchedAtMs, nowMs)
  let eta
  if (left != null) eta = `${formatEta(left)} left`
  else if (item.overrun) eta = 'slower than any past run'
  else eta = 'no history yet'
  const basis = item.typical_s != null && item.samples >= 3 ? ` · typical ${formatEta(item.typical_s)} over ${item.samples} runs` : ''
  return `running ${formatEta(elapsed)} · ${eta}${basis}`
}

/**
 * Headline for the header pill and the Watchers banner. data = the /api/watchers/running payload (or null before the first answer).
 * tone: idle | busy | unknown. allClear: seconds until every counted run is done, or null when any of them has no ETA.
 */
export function runningHeadline(data, fetchedAtMs, nowMs) {
  if (!data) return { tone: 'unknown', label: '…', detail: 'checking what is running', allClear: null }
  const stalled = data.stalled_count ? ` · ${data.stalled_count} old marker${data.stalled_count > 1 ? 's' : ''} ignored` : ''
  if (!data.busy) {
    const brief = data.brief_count ? ` · ${data.brief_count} brief job${data.brief_count > 1 ? 's' : ''} in flight` : ''
    return { tone: 'idle', label: 'IDLE', detail: `no long-running job${brief}${stalled}`, allClear: 0 }
  }
  const running = data.items.filter(i => i.state === 'RUNNING')
  const lefts = running.map(i => remainingNow(i, fetchedAtMs, nowMs))
  const known = lefts.length > 0 && lefts.every(x => x != null)
  const allClear = known ? Math.max(...lefts) : null
  const when = known ? `all clear in about ${formatEta(allClear)}` : 'time left unknown for some'
  const slow = data.any_overrun ? ' · slower than usual' : ''
  return { tone: 'busy', label: `RUNNING ${running.length}`, detail: `${when}${slow}${stalled}`, allClear }
}
